import { useEffect, useState } from "react";
import { FileText, Film, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, DOMAINES, domaineLabel, fmtDate, mediaUrl } from "@/lib/api";
import MediaUpload from "@/components/MediaUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const blank = { titre: "", domaine: "electricite", description: "", image_url: "", video_url: "", medias: [], ville: "", consentement_client: true, publie: true };

export default function AdminVitrine() {
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/admin/realisations").then(({ data }) => setItems(data));
    api.get("/admin/leads").then(({ data }) => setLeads(data));
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.titre.trim()) return toast.error("Titre requis");
    if (!form.consentement_client) return toast.error("Consentement RGPD du client requis pour publier");
    const { id, created_at, ...payload } = form;
    if (editId) await api.put(`/admin/realisations/${editId}`, payload);
    else await api.post("/admin/realisations", payload);
    toast.success("Réalisation enregistrée");
    setForm(blank);
    setEditId(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-vitrine">
      <div>
        <div className="bk-label mb-2">CMS</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Vitrine & leads</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
          <div className="bk-label">{editId ? "Modifier" : "Nouvelle réalisation"}</div>
          <Input data-testid="real-titre-input" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Titre" className="h-11" />
          <select
            data-testid="real-domaine-select"
            value={form.domaine}
            onChange={(e) => setForm({ ...form, domaine: e.target.value })}
            className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm"
          >
            {DOMAINES.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
          <Textarea data-testid="real-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Description du chantier" />
          <Input data-testid="real-image-input" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL photo de couverture (après)" className="h-11" />
          <MediaUpload
            medias={form.medias || []}
            onChange={(medias) => setForm({ ...form, medias })}
            endpoint="/admin/media"
            testid="real-medias"
            label="Photos, PDF et vidéo courte du chantier"
            max={10}
          />
          <Input data-testid="real-ville-input" value={form.ville || ""} onChange={(e) => setForm({ ...form, ville: e.target.value })} placeholder="Ville" className="h-11" />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input data-testid="real-consent-checkbox" type="checkbox" checked={form.consentement_client} onChange={(e) => setForm({ ...form, consentement_client: e.target.checked })} className="accent-[#005CFF]" />
            Consentement RGPD du client obtenu
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.publie} onChange={(e) => setForm({ ...form, publie: e.target.checked })} className="accent-[#005CFF]" /> Publié
          </label>
          <div className="flex gap-2">
            <Button data-testid="real-save-btn" onClick={save} className="bg-[#005CFF] hover:bg-[#0047CC]">
              <Plus size={15} className="mr-1" /> Enregistrer
            </Button>
            {editId && <Button variant="outline" onClick={() => { setEditId(null); setForm(blank); }}>Annuler</Button>}
          </div>
        </div>

        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} data-testid={`admin-real-${r.id}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
              <button
                onClick={() => {
                  setEditId(r.id);
                  setForm({ ...r, medias: r.medias || [] });
                }}
                className="text-left min-w-0 flex items-center gap-3"
              >
                {r.image_url && <img src={r.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-900 truncate">{r.titre}</span>
                  <span className="block text-xs text-slate-500">{domaineLabel(r.domaine)} · {r.publie ? "publié" : "masqué"}</span>
                </span>
              </button>
              <button
                data-testid={`admin-real-delete-${r.id}`}
                onClick={async () => {
                  await api.delete(`/admin/realisations/${r.id}`);
                  load();
                }}
                className="text-slate-300 hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-leads">
        <div className="bk-label mb-3">Leads « devis similaire » ({leads.length})</div>
        <div className="space-y-2">
          {leads.map((l) => (
            <div key={l.id} data-testid={`admin-lead-${l.id}`} className="border-b border-slate-100 pb-3">
              <div className="text-sm text-slate-800">
                {l.client} — {l.message}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{fmtDate(l.created_at)}</div>
              {l.medias?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {l.medias.map((m) => (
                    <a
                      key={m.url}
                      href={mediaUrl(m.url)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`lead-media-${m.url.split("/").pop()}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 hover:border-[#005CFF] transition-colors"
                    >
                      {m.type?.startsWith("video/") ? <Film size={12} /> : m.type === "application/pdf" ? <FileText size={12} /> : <ImageIcon size={12} />}
                      <span className="max-w-[160px] truncate">{m.nom || "pièce jointe"}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          {leads.length === 0 && <p className="text-sm text-slate-500">Aucun lead.</p>}
        </div>
      </div>
    </div>
  );
}
